import axios, { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { userInformation } from '@redux/actions/User';
import { ApiResponse } from 'further-types/api';
import { Api } from 'further-types/further-user';
import { UserType } from 'further-types/user';
import { fetchCurrentUserInfo } from './useCurrentUserInfo';
import { useHomepageRedirect } from 'helpers/user/homepageRedirects';
import { useWhiteLabellingContext } from 'contexts/WhiteLabellingContext';
import { AccountType } from 'further-types/investor';

export function useAuthGateCopyProfile(
  params: { firmId?: string },
  onAccountsListSuccess?: (
    data: Api.ListAccountsToCopyProfileFromResponse,
  ) => void,
) {
  const accountsList = useQuery<Api.ListAccountsToCopyProfileFromResponse>({
    queryKey: ['auth-gate', 'list-accounts-to-copy-profile-from'],
    queryFn: async () => {
      const response = await axios.get<
        ApiResponse<Api.ListAccountsToCopyProfileFromResponse>
      >(`/further-user/list-accounts-to-copy-profile-from`, { params });

      const data = response.data.data;
      onAccountsListSuccess?.(data);
      return data;
    },
    enabled: !!params.firmId,
  });

  const copyProfile = useMutation({
    mutationFn: async (params: {
      copyFromInvestorId: string;
      firmId?: string;
      allowDuplicateInvestorInFirm?: boolean;
    }) => {
      const response = await axios.post<
        ApiResponse<Api.CopyProfileToNewInvestorResponse>
      >(`/further-user/copy-profile-to-new-investor`, {
        copyFromInvestorId: params.copyFromInvestorId,
        firmId: params.firmId,
        allowDuplicateInvestorInFirm: params.allowDuplicateInvestorInFirm,
      });
      return response.data;
    },
    // onError intentionally omitted — CopyProfile component handles all error paths
  });

  const createEmptyInvestor = useMutation({
    mutationFn: async (params: {
      firmId?: string;
      accountType?: AccountType;
    }) => {
      const response = await axios.post<
        ApiResponse<Api.CreateEmptyInvestorResponse>
      >(`/further-user/create-empty-investor`, {
        firmId: params.firmId,
        accountType: params.accountType,
      });
      return response.data;
    },
    onError: (error: AxiosError<ApiResponse<any>>) => {
      message.error(error?.response?.data?.responseMsg);
    },
  });

  return { accountsList, copyProfile, createEmptyInvestor };
}

export function useAuthGateChooseAccount(
  params: {
    firmIds?: { eisFirmId?: string; lpFirmId?: string };
  },
  { disabled }: { disabled?: boolean } = {},
) {
  const dispatch = useDispatch();
  const redirect = useHomepageRedirect();
  const queryClient = useQueryClient();
  const { isSubdomain } = useWhiteLabellingContext();

  const isFirmIdSupplied =
    !!params.firmIds?.eisFirmId || !!params.firmIds?.lpFirmId;

  const accountsList = useQuery({
    queryKey: ['auth-gate', 'list-accounts-to-access'],
    queryFn: async () => {
      const response = await axios.get<
        ApiResponse<Api.ListAccountsToAccessResponse>
      >(`/further-user/list-accounts-to-access`, {
        params: { ...params.firmIds, includeCurrent: true },
      });
      return response.data.data;
    },
    enabled: isFirmIdSupplied && !disabled,
  });

  const totalAccountsOnFirm =
    (accountsList.data?.investors.length ?? 0) +
    (accountsList.data?.adviser ? 1 : 0) +
    (accountsList.data?.orgUser ? 1 : 0);

  const noAccountsToSelect =
    !isFirmIdSupplied ||
    (!accountsList.isLoading &&
      accountsList.data?.investors.length === 0 &&
      !accountsList.data?.adviser &&
      !accountsList.data?.orgUser);

  const hasMultipleAccounts =
    isFirmIdSupplied && !accountsList.isLoading && totalAccountsOnFirm > 1;

  const hasSingleAccountInFirm =
    isFirmIdSupplied && !accountsList.isLoading && totalAccountsOnFirm === 1;

  const accessAccount = useMutation({
    mutationFn: async (mutationParams: {
      accountId?: string;
      userType: UserType;
    }) => {
      await axios.post<ApiResponse<Api.AccessAccountResponse>>(
        `/further-user/access-account`,
        {
          accountId: mutationParams.accountId,
          userType: mutationParams.userType,
          firmIds: params.firmIds,
        },
      );
      queryClient.clear();

      const currentUserInfo = await fetchCurrentUserInfo({ isSubdomain });
      const user = {
        ...currentUserInfo,
        isAuthenticated: true,
      };

      await dispatch<any>(userInformation(user));
      redirect({ user });
    },
    onError: (error: AxiosError<ApiResponse<any>>) => {
      message.error(error?.response?.data?.responseMsg);
    },
  });

  return {
    accountsList,
    noAccountsToSelect,
    hasMultipleAccounts,
    hasSingleAccountInFirm,
    accessAccount,
  };
}
